import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { Search, Settings2, Trash2, AlertTriangle, Clock, Undo2, Eye } from "lucide-react";
import PedidoDocumentoDialog from "@/components/PedidoDocumentoDialog";
import FacturaArchivos from "@/components/FacturaArchivos";
import RemisionArchivos from "@/components/RemisionArchivos";
import IncidenciaPanel from "@/components/IncidenciaPanel";
import { money, fechaMX, fechaHoraMX } from "@/lib/format";
import ReporteModulo from "@/components/ReporteModulo";
import FiltroSelect, { labelDe } from "@/components/FiltroSelect";
import { useVendedoresActivos } from "@/hooks/useVendedoresActivos";
import AgrupadoPorFecha from "@/components/AgrupadoPorFecha";
import { useRefetchOnFocus } from "@/hooks/useRefetchOnFocus";

const getProgress = (e) => ({ "Cotización aprobada": 12, "Pedido generado": 25, "Producción": 38, "Entregado": 50, "Facturación": 62, "Vencimiento": 75, "Pago": 88, "Pedido cerrado": 100 }[e] || 0);
const ESTATUS_OPCIONES = ["Cotización aprobada", "Pedido generado", "Producción", "Entregado", "Facturación", "Vencimiento", "Pago", "Pedido cerrado"];
const pagoStatus = (p) => {
  if (p.estatus_pago === "Cobrado" || p.fecha_pago) return "Cobrado";
  if (p.estatus_pago === "Pendiente de cobro" || p.factura || p.fecha_facturacion) return "Pendiente de cobro";
  return "Sin facturar";
};
const pagoBadge = (s) => (s === "Cobrado" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-transparent" : s === "Pendiente de cobro" ? "bg-amber-100 text-amber-700 hover:bg-amber-100 border-transparent" : "bg-slate-100 text-slate-600 hover:bg-slate-100 border-transparent");
const fechaHora = (iso) => fechaHoraMX(iso);
const DIAS_AVISO = 5;
const diasParaVencer = (p) => {
  if (!p.fecha_vencimiento) return null;
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  const venc = new Date(p.fecha_vencimiento + "T00:00:00");
  return Math.round((venc - hoy) / 86400000);
};
const noPagado = (p) => !(p.estatus_pago === "Cobrado" || p.fecha_pago || p.estatus === "Pago" || p.estatus === "Pedido cerrado");
const esVencida = (p) => { if (!p.fecha_vencimiento || !noPagado(p)) return false; return diasParaVencer(p) < 0; };
const esPorVencer = (p, dias = DIAS_AVISO) => { if (!p.fecha_vencimiento || !noPagado(p)) return false; const d = diasParaVencer(p); return d >= 0 && d <= dias; };
const stageIndex = (e) => ESTATUS_OPCIONES.indexOf(e);
const pedVend = (p) => p.vendedor?.full_name;
const pedFecha = (p) => (p.created_at ? String(p.created_at).slice(0, 10) : null);

export default function Pedidos() {
  const { isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get("cliente") || "");
  const [sel, setSel] = useState(null);
  const [verPed, setVerPed] = useState(null);
  const [saving, setSaving] = useState(false);
  const [timeline, setTimeline] = useState({ vida: [], cambios: [] });
  const [confirmData, setConfirmData] = useState(null);
  const [diasAviso, setDiasAviso] = useState(5);
  const [vendSel, setVendSel] = useState("todos");
  const [estSel, setEstSel] = useState("todos");
  const vendedores = useVendedoresActivos(isAdmin, pedidos, pedVend);

  useEffect(() => { api.get("/parametros").then(({ data }) => setDiasAviso(Number(data?.dias_aviso_vencimiento) || 5)).catch(() => {}); }, []);

  const pedirConfirmacion = (actual, nuevo, onConfirm) => {
    if (!nuevo || nuevo === actual) return;
    if (stageIndex(nuevo) < stageIndex(actual)) setConfirmData({ actual, nuevo, onConfirm });
    else onConfirm();
  };

  const openGestion = async (p) => {
    setSel({ ...p });
    setTimeline({ vida: [], cambios: [] });
    try { const { data } = await api.get(`/pedidos/${p.id}/timeline`); setTimeline(data || { vida: [], cambios: [] }); } catch { /* noop */ }
  };

  const fetch = async () => {
    try { setLoading(true); const { data } = await api.get("/pedidos"); setPedidos(data || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch(); }, []);
  useRefetchOnFocus(fetch);

  const guardar = async () => {
    setSaving(true);
    try {
      const payload = {
        estatus: sel.estatus, fecha_entrega: sel.fecha_entrega || null, factura: sel.factura,
        fecha_facturacion: sel.fecha_facturacion || null, credito: sel.credito, fecha_vencimiento: sel.fecha_vencimiento || null,
        fecha_pago: sel.fecha_pago || null, observaciones_administrativas: sel.observaciones_administrativas,
      };
      const { data } = await api.put(`/pedidos/${sel.id}`, payload);
      setPedidos((prev) => prev.map((x) => (x.id === sel.id ? { ...x, ...data } : x)));
      if (data.estatus && data.estatus !== (sel.estatus || "")) {
        toast.success(`Pedido actualizado. El proceso avanzó a "${data.estatus}".`);
      } else {
        toast.success("Pedido actualizado");
      }
      setSel(null);
    } catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
    finally { setSaving(false); }
  };
  const cambiarEstatus = async (id, estatus) => {
    try { const { data } = await api.put(`/pedidos/${id}`, { estatus }); setPedidos((prev) => prev.map((x) => (x.id === id ? { ...x, ...data } : x))); toast.success("Pedido actualizado"); }
    catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); fetch(); }
  };
  const revertir = async (p) => {
    if (!window.confirm(`¿Revertir el pedido ${p.numero_pedido} a cotización?\n\nSe eliminará el pedido, su remisión y su comisión, y la cotización volverá a "Pendiente de autorización". Esta acción no se puede deshacer.`)) return;
    try { await api.post(`/pedidos/${p.id}/revertir`); setPedidos((prev) => prev.filter((x) => x.id !== p.id)); toast.success(`Pedido ${p.numero_pedido} revertido a cotización.`); }
    catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };
  const eliminar = async (id) => {    if (!window.confirm("¿Eliminar este pedido? Se borrarán su remisión y comisión asociadas.")) return;
    try { await api.delete(`/pedidos/${id}`); setPedidos((p) => p.filter((x) => x.id !== id)); toast.success("Pedido eliminado"); }
    catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };

  const filtered = pedidos.filter((p) => p.estatus !== "Pedido cerrado").filter((p) => (!isAdmin || vendSel === "todos" || (pedVend(p) || "") === vendSel) && (estSel === "todos" || p.estatus === estSel)).filter((p) => !search || `${p.numero_pedido} ${p.cliente} ${p.estatus}`.toLowerCase().includes(search.toLowerCase()));
  const vencidas = filtered.filter(esVencida);
  const porVencer = filtered.filter((p) => esPorVencer(p, diasAviso));
  useEffect(() => { if (!loading && vencidas.length > 0) toast.warning(`${vencidas.length} factura(s) vencida(s) sin pago`, { description: "Revisa la cobranza pendiente en Pedidos." }); }, [loading, vencidas.length]);
  useEffect(() => { if (!loading && porVencer.length > 0) toast.warning(`${porVencer.length} factura(s) por vencer`, { description: `Vencen en los próximos ${diasAviso} días.` }); }, [loading, porVencer.length]);

  const renderRow = (p) => {
                    const ps = pagoStatus(p);
                    return (
                      <TableRow key={p.id} className={esVencida(p) ? "bg-red-50 hover:bg-red-50" : esPorVencer(p, diasAviso) ? "bg-amber-50 hover:bg-amber-50" : ""}>
                        <TableCell className="font-mono text-xs font-semibold">{p.numero_pedido}{p.incidencia && p.incidencia.estado !== "resuelta" && <Badge className="ml-2 bg-red-100 text-red-700 border-transparent font-sans" data-testid={`pedido-incidencia-${p.id}`}>Incidencia</Badge>}</TableCell>
                        <TableCell className="font-medium">{p.cliente}</TableCell>
                        <TableCell>{p.vendedor?.full_name || "-"}</TableCell>
                        <TableCell>
                          {isAdmin ? (
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <Select value={p.estatus || ""} onValueChange={(v) => pedirConfirmacion(p.estatus, v, () => cambiarEstatus(p.id, v))}>
                                  <SelectTrigger className="h-8 text-xs" data-testid={`pedido-estatus-inline-${p.id}`}><SelectValue placeholder="Sin estatus" /></SelectTrigger>
                                  <SelectContent>
                                    {ESTATUS_OPCIONES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                                <span className="text-xs text-muted-foreground w-9 text-right">{getProgress(p.estatus)}%</span>
                              </div>
                              <Progress value={getProgress(p.estatus)} className="h-2" />
                            </div>
                          ) : (
                            <>
                              <div className="flex justify-between text-xs mb-1"><span>{p.estatus || "Sin estatus"}</span><span>{getProgress(p.estatus)}%</span></div>
                              <Progress value={getProgress(p.estatus)} className="h-2" />
                            </>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Badge className={pagoBadge(ps)}>{ps}</Badge>
                            {esVencida(p) && <Badge variant="destructive" className="gap-1" data-testid={`pedido-vencida-${p.id}`}><AlertTriangle className="h-3 w-3" /> Vencida</Badge>}
                            {esPorVencer(p, diasAviso) && <Badge className="gap-1 bg-amber-100 text-amber-700 hover:bg-amber-100 border-transparent" data-testid={`pedido-porvencer-${p.id}`}><Clock className="h-3 w-3" /> {diasParaVencer(p) === 0 ? "Vence hoy" : `Vence en ${diasParaVencer(p)}d`}</Badge>}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{money(p.subtotal)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="outline" size="icon" title="Ver pedido" onClick={() => setVerPed(p)} data-testid={`pedido-ver-${p.id}`}><Eye className="h-4 w-4" /></Button>
                            {isAdmin && <Button variant="outline" size="icon" title="Gestionar" onClick={() => openGestion(p)} data-testid={`pedido-gestionar-${p.id}`}><Settings2 className="h-4 w-4" /></Button>}
                            {isAdmin && p.cotizacion_id && <Button variant="outline" size="icon" title="Revertir a cotización" className="text-amber-600" onClick={() => revertir(p)} data-testid={`pedido-revertir-${p.id}`}><Undo2 className="h-4 w-4" /></Button>}
                            {isAdmin && <Button variant="destructive" size="icon" title="Eliminar" onClick={() => eliminar(p.id)}><Trash2 className="h-4 w-4" /></Button>}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  };

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="pedidos-page">
      <div><h1 className="text-3xl font-bold tracking-tight">Pedidos</h1><p className="text-muted-foreground mt-2">Seguimiento de producción, facturación y cobranza.</p></div>
      {vencidas.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4" data-testid="pedidos-vencidas-alert">
          <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold text-red-800">{vencidas.length} factura(s) vencida(s) sin pago</p>
            <p className="text-sm text-red-700">{vencidas.map((v) => v.numero_pedido).join(", ")}</p>
          </div>
        </div>
      )}
      {porVencer.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="pedidos-porvencer-alert">
          <Clock className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold text-amber-800">{porVencer.length} factura(s) por vencer (próximos {diasAviso} días)</p>
            <p className="text-sm text-amber-700">{porVencer.map((v) => `${v.numero_pedido} (${diasParaVencer(v) === 0 ? "vence hoy" : `${diasParaVencer(v)}d`})`).join(", ")}</p>
          </div>
        </div>
      )}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="relative flex-1 max-w-lg">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input type="search" placeholder="Buscar por pedido, cliente o estatus..." className="pl-8" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="pedido-search" />
            </div>
            <div className="flex flex-wrap items-center gap-2">
            {isAdmin && <FiltroSelect value={vendSel} onChange={setVendSel} todosLabel="Todos los vendedores" options={vendedores} testid="pedido-filtro-vendedor" />}
            <FiltroSelect value={estSel} onChange={setEstSel} todosLabel="Todos los estatus" options={ESTATUS_OPCIONES.filter((o) => o !== "Pedido cerrado")} testid="pedido-filtro-estatus" />
            {isAdmin && (
            <ReporteModulo
              titulo="REPORTE DE PEDIDOS"
              filenameBase="Reporte pedidos"
              prefix="rep-ped"
              triggerTestId="pedido-reporte-btn"
              buttonVariant="outline"
              rows={filtered}
              getFecha={pedFecha}
              groupBy={(p) => p.vendedor?.full_name || "Sin asignar"}
              filtros={[{ label: "Vendedor", value: labelDe(vendSel, vendedores) }, { label: "Estatus", value: labelDe(estSel, ESTATUS_OPCIONES) }, { label: "Búsqueda", value: search }]}
              columns={[
                { header: "Pedido", value: (p) => p.numero_pedido, mono: true },
                { header: "Cliente", value: (p) => p.cliente },
                { header: "Vendedor", value: (p) => p.vendedor?.full_name || "-" },
                { header: "Estatus", value: (p) => p.estatus || "-" },
                { header: "Pago", value: (p) => pagoStatus(p) },
                { header: "Subtotal", align: "right", value: (p) => money(p.subtotal) },
              ]}
              subtotal={(rows) => money(rows.reduce((s, p) => s + Number(p.subtotal || 0), 0))}
              totales={(rows) => [{ label: "Total subtotal", value: money(rows.reduce((s, p) => s + Number(p.subtotal || 0), 0)) }]}
            />
            )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <p className="text-center text-muted-foreground py-12">Cargando...</p> : (
            <AgrupadoPorFecha
              rows={filtered}
              getFecha={pedFecha}
              testidPrefix="pedido"
              emptyText="No hay pedidos en este periodo."
              resumen={(list) => <>Subtotal: <span className="font-semibold text-foreground">{money(list.reduce((s, p) => s + Number(p.subtotal || 0), 0))}</span></>}
              renderRows={(list) => (
                <div className="border rounded-md overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow><TableHead>Pedido</TableHead><TableHead>Cliente</TableHead><TableHead>Vendedor</TableHead><TableHead className="w-64">Progreso</TableHead><TableHead>Pago</TableHead><TableHead className="text-right">Subtotal</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
                    <TableBody>{list.map(renderRow)}</TableBody>
                  </Table>
                </div>
              )}
            />
          )}
        </CardContent>
      </Card>

      <PedidoDocumentoDialog pedido={verPed} onClose={() => setVerPed(null)} />

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Gestionar pedido {sel?.numero_pedido}</DialogTitle><DialogDescription>{sel?.cliente}</DialogDescription></DialogHeader>
          {sel && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label>Estatus de producción</Label>
                <Select value={sel.estatus || ""} onValueChange={(v) => pedirConfirmacion(sel.estatus, v, () => setSel({ ...sel, estatus: v }))}>
                  <SelectTrigger data-testid="pedido-estatus"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    {ESTATUS_OPCIONES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Progress value={getProgress(sel.estatus)} className="h-2 mt-2" />
              </div>
              <div className="space-y-2"><Label>Fecha de entrega</Label><Input type="date" value={sel.fecha_entrega || ""} onChange={(e) => setSel({ ...sel, fecha_entrega: e.target.value })} /></div>
              <div className="space-y-2"><Label>Factura</Label><Input value={sel.factura || ""} placeholder="Folio de factura" onChange={(e) => setSel({ ...sel, factura: e.target.value })} /></div>
              <IncidenciaPanel pedido={sel} onChange={(d) => { setSel((s) => ({ ...s, incidencia: d.incidencia, subtotal: d.subtotal, historial: d.historial })); setPedidos((prev) => prev.map((x) => (x.id === d.id ? { ...x, ...d } : x))); }} />
              <FacturaArchivos pedido={sel} estatusGuardado={pedidos.find((x) => x.id === sel.id)?.estatus} onChange={(d) => { setSel((s) => ({ ...s, ...d })); setPedidos((prev) => prev.map((x) => (x.id === d.id ? { ...x, ...d } : x))); }} />
              <RemisionArchivos pedido={sel} estatusGuardado={pedidos.find((x) => x.id === sel.id)?.estatus} onChange={(d) => { setSel((s) => ({ ...s, remisiones: d.remisiones, remision_enviada_at: d.remision_enviada_at, historial: d.historial })); setPedidos((prev) => prev.map((x) => (x.id === d.id ? { ...x, ...d } : x))); }} />
              <div className="space-y-2"><Label>Fecha de facturación</Label><Input type="date" value={sel.fecha_facturacion || ""} onChange={(e) => setSel({ ...sel, fecha_facturacion: e.target.value })} /></div>
              <div className="space-y-2"><Label>Crédito</Label><Input value={sel.credito || ""} placeholder="Ej. 30 días" onChange={(e) => setSel({ ...sel, credito: e.target.value })} /></div>
              <div className="space-y-2"><Label>Fecha de vencimiento</Label><Input type="date" value={sel.fecha_vencimiento || ""} onChange={(e) => setSel({ ...sel, fecha_vencimiento: e.target.value })} /></div>
              <div className="space-y-2"><Label>Fecha de pago</Label><Input type="date" value={sel.fecha_pago || ""} onChange={(e) => setSel({ ...sel, fecha_pago: e.target.value })} /></div>
              <div className="space-y-2 md:col-span-2"><Label>Monto del pedido (subtotal, sin IVA)</Label><Input value={money(sel.subtotal)} readOnly disabled data-testid="pedido-subtotal" /><p className="text-xs text-muted-foreground">Viene de la cotización autorizada. Para cambiarlo, revierte a cotización (↺), edítala y vuelve a autorizarla.</p></div>
              <div className="space-y-2 md:col-span-2"><Label>Observaciones administrativas</Label><Textarea value={sel.observaciones_administrativas || ""} onChange={(e) => setSel({ ...sel, observaciones_administrativas: e.target.value })} /></div>

              <div className="md:col-span-2 border-t pt-4 mt-1" data-testid="pedido-vida">
                <h4 className="font-semibold text-sm mb-3">Vida del pedido</h4>
                <ol className="relative border-l border-slate-200 ml-2 space-y-4">
                  {(timeline.vida || []).map((e, i) => (
                    <li key={i} className="ml-4">
                      <span className="absolute -left-[7px] mt-1 h-3 w-3 rounded-full bg-primary ring-4 ring-white" />
                      <div className="text-xs text-muted-foreground">{fechaMX(e.fecha, "—")}</div>
                      <div className="text-sm font-medium">{e.titulo}</div>
                      {e.detalle && <div className="text-xs text-muted-foreground">{e.detalle}</div>}
                    </li>
                  ))}
                </ol>
              </div>

              <div className="md:col-span-2 border-t pt-4 mt-1" data-testid="pedido-historial">
                <h4 className="font-semibold text-sm mb-3">Historial de cambios</h4>
                {(timeline.cambios || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">Aún no hay cambios registrados. Los próximos cambios que hagas aparecerán aquí con su fecha y hora.</p>
                ) : (
                  <ul className="space-y-2">
                    {(timeline.cambios || []).map((c, i) => (
                      <li key={i} className="text-xs flex flex-col sm:flex-row sm:gap-2">
                        <span className="text-muted-foreground whitespace-nowrap">{fechaHora(c.ts)}</span>
                        <span><span className="font-semibold">{c.campo}:</span> {c.de || "—"} → {c.a || "—"} <span className="text-muted-foreground">· {c.usuario}</span></span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setSel(null)}>Cancelar</Button><Button onClick={guardar} disabled={saving} data-testid="pedido-save">{saving ? "Guardando..." : "Guardar"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmData} onOpenChange={(o) => !o && setConfirmData(null)}>
        <AlertDialogContent data-testid="rollback-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Regresar a una etapa anterior?</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a cambiar de <span className="font-semibold">"{confirmData?.actual || "Sin estatus"}"</span> a <span className="font-semibold">"{confirmData?.nuevo}"</span>. Se borrarán las fechas y datos de las etapas posteriores (facturación, folio, vencimiento y pago). ¿Deseas continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="rollback-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { confirmData?.onConfirm?.(); setConfirmData(null); }} data-testid="rollback-confirm">Sí, regresar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
